import { Box, Stack, Typography } from "@mui/material";
import { purple } from "@mui/material/colors";
import type { GridColDef, GridRowParams } from "@mui/x-data-grid-premium";
import { useCallback } from "react";
import type { EntriliaProduct } from "@entrilia/shared/api/types";
import DataGrid from "@entrilia/shared/components/grid/DataGrid";
import StatusChip from "@entrilia/shared/components/StatusChip";
import CopyToBufferButton from "@entrilia/shared/components/inputs/CopyToBufferButton";
import { makeModeAwareAccent } from "@entrilia/shared/utilities/colorHelper";
import { convertISODateTime } from "@entrilia/shared/utilities/dateUtils";
import { autoFormatCamelCase } from "@entrilia/shared/utilities/stringHelper";
import { nameof } from "@entrilia/shared/utilities/typeHelper";
import type { ActivityLogRecord } from "../../../../../api/types/activityLogTypes";
import { entriliaProductCaptionMap } from "../../../../../utilities/enumCaptions";
import GridPaginationFooter from "../../../../common/GridPaginationFooter";
import type { ActivityLogViewPagingState } from "./activityLogViewState";
import ActivityRowGridDetail from "./ActivityRowGridDetail";

const columns: GridColDef<ActivityLogRecord>[] = [
  {
    field: nameof<ActivityLogRecord>("timestamp"),
    headerName: "Date",
    type: "dateTime",
    flex: 1,
    minWidth: 160,
    valueFormatter: (value: string | undefined) => convertISODateTime(value),
  },
  {
    field: nameof<ActivityLogRecord>("userName"),
    headerName: "User",
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => (
      <Stack direction="row" spacing={1} sx={{ alignItems: "center", minWidth: 0 }}>
        <Typography noWrap>{row.userName}</Typography>
        {row.assistantTaskId && (
          <Box sx={{ flexShrink: 0 }}>
            <StatusChip label="AI task" color={(t) => makeModeAwareAccent(t, purple)} height="smaller" />
          </Box>
        )}
      </Stack>
    ),
  },
  {
    field: nameof<ActivityLogRecord>("eventArea"),
    headerName: "Product Area",
    flex: 1,
    minWidth: 200,
    valueFormatter: (value: EntriliaProduct | undefined) =>
      value ? entriliaProductCaptionMap[value] || autoFormatCamelCase(value) : "",
  },
  {
    field: nameof<ActivityLogRecord>("resourceType"),
    headerName: "Resource Type",
    flex: 1,
    minWidth: 200,
    valueFormatter: (value: string | undefined) => (value ? autoFormatCamelCase(value) : ""),
  },
  {
    field: nameof<ActivityLogRecord>("resourceId"),
    headerName: "Resource ID",
    flex: 1,
    minWidth: 200,
    renderCell: ({ row }) => (
      <Stack direction="row" spacing={0.5} sx={{ alignItems: "center", width: "calc(100% - 24px)" }}>
        <Typography noWrap>{row.resourceId}</Typography>
        <CopyToBufferButton textToCopy={row.resourceId ?? ""} />
      </Stack>
    ),
  },
  {
    field: nameof<ActivityLogRecord>("eventType"),
    headerName: "Event Type",
    flex: 1,
    minWidth: 160,
    valueFormatter: (value: string | undefined) => (value ? autoFormatCamelCase(value) : ""),
  },
  {
    field: nameof<ActivityLogRecord>("description"),
    headerName: "Description",
    flex: 1.5,
    minWidth: 200,
  },
];

interface Props {
  rows: ActivityLogRecord[];
  isLoading: boolean;
  pagingState: ActivityLogViewPagingState;
  onPageIndexChange: (newPageIndex: number) => void;
  onRowsPerPageChange: (newRowsPerPage: number) => void;
}

const getDetailPanelHeight = () => "auto" as const;

const ActivityLogGrid = ({ rows, isLoading, pagingState, onPageIndexChange, onRowsPerPageChange }: Props) => {
  const getDetailPanelContent = useCallback(
    ({ row }: GridRowParams<ActivityLogRecord>) =>
      row.details ? <ActivityRowGridDetail details={row.details} /> : undefined,
    []
  );

  return (
    <DataGrid<ActivityLogRecord>
      columns={columns}
      rows={rows}
      loading={isLoading}
      getDetailPanelContent={getDetailPanelContent}
      getDetailPanelHeight={getDetailPanelHeight}
      disableColumnReorder
      disableColumnSorting
      hideFooter={false}
      slots={{
        footer: () =>
          pagingState.totalPages !== undefined ? (
            <GridPaginationFooter
              totalPages={pagingState.totalPages}
              rowsPerPage={pagingState.rowsPerPage}
              pageIndex={pagingState.pageIndex}
              align="left"
              onPageIndexChange={onPageIndexChange}
              onRowsPerPageChange={onRowsPerPageChange}
            />
          ) : null,
      }}
      containerSx={{ height: "calc(100% - 64px)" }}
    />
  );
};

export default ActivityLogGrid;
